import React, {useLayoutEffect} from 'react';
import ReactDOM from 'react-dom';
import {createGlobalStyle} from 'styled-components';
import {FrameContextConsumer} from 'context/frame';
import {TailwindService} from 'services/TailwindService';
import globalCss from '../../../index.css?inline';

interface Props {
  children: React.ReactNode;
  customStyles?: string;
  customStylesLink?: string;
}

const GlobalStyle = createGlobalStyle<{customStyles?: string}>`
  ${({customStyles}) => customStyles}
`;

export function InjectingStylesProvider({
  children,
  customStyles,
  customStylesLink,
}: Props) {
  return (
    <FrameContextConsumer>
      {({document: iframeDocument}) => (
        <InjectingStylesProviderInner
          iframeDocument={iframeDocument}
          customStyles={customStyles}
          customStylesLink={customStylesLink}
        >
          {children}
        </InjectingStylesProviderInner>
      )}
    </FrameContextConsumer>
  );
}

interface InjectingStylesProviderInnerProps extends Props {
  iframeDocument: Document | null;
}

function InjectingStylesProviderInner({
  children,
  iframeDocument,
  customStyles,
  customStylesLink,
}: InjectingStylesProviderInnerProps) {
  useLayoutEffect(() => {
    if (!iframeDocument) {
      return;
    }

    try {
      const parentDocument = window.parent.document;

      parentDocument.head.querySelectorAll('style').forEach(styleEl => {
        const content = styleEl.textContent || '';
        if (content.includes('[data-sonner-toaster]')) {
          iframeDocument.head.append(styleEl.cloneNode(true));
        }
      });

      const googleFonts = [
        '<link rel="preconnect" href="https://fonts.googleapis.com">',
        '<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>',
        '<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@200;300;400;500;600;700;800;900&display=swap" rel="stylesheet">',
      ];

      googleFonts.forEach(linkHtml => {
        const tempDiv = iframeDocument.createElement('div');
        tempDiv.innerHTML = linkHtml;
        const linkElement = tempDiv.firstElementChild as HTMLLinkElement;
        if (linkElement) {
          iframeDocument.head.appendChild(linkElement);
        }
      });

      const tailwindService = TailwindService.getInstance();
      tailwindService.setTargetDocument(iframeDocument);
    } catch (error) {
      console.error('[InjectingStylesProvider] Error injecting styles:', error);
    }
  }, [iframeDocument, customStyles, customStylesLink]);

  return (
    <React.Fragment>
      {ReactDOM.createPortal(
        <style>{globalCss}</style>,
        iframeDocument?.head as HTMLElement,
      )}
      {customStylesLink &&
        ReactDOM.createPortal(
          <link rel="stylesheet" href={customStylesLink} />,
          iframeDocument?.head as HTMLElement,
        )}
      <GlobalStyle customStyles={customStyles} />
      {children}
    </React.Fragment>
  );
}
